import { formatDayAndMonth, formatTime } from '@/components/agenda/meetingFormat.ts';
import { getLocale } from '@/paraglide/runtime.js';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/* Within a week a day is named; from there on it is dated. */
const NAMED_DAYS = 7;

/**
 * When the next 1:1 is, the way somebody glancing down a list wants it:
 * "morgen · 11:00", "vrijdag · 11:00" later this week, "di 13 oktober · 11:00"
 * from next week on.
 */
export function formatNextOneOnOne(isoTimestamp: string, now = new Date()): string {
  return `${nextOneOnOneDay(isoTimestamp, now)} · ${formatTime(isoTimestamp)}`;
}

function nextOneOnOneDay(isoTimestamp: string, now: Date): string {
  const date = new Date(isoTimestamp);
  const days = daysFromToday(isoTimestamp, now);
  const locale = getLocale();
  if (days === 0 || days === 1) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day');
  }
  if (days > 1 && startOfWeek(date) === startOfWeek(now)) {
    return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
  }
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  }).format(date);
}

/** "15 sep" — a date in passing, as in "Bijgewerkt op 15 sep". */
export function formatShortDay(isoTimestamp: string): string {
  return new Intl.DateTimeFormat(getLocale(), { day: 'numeric', month: 'short' })
    .format(new Date(isoTimestamp))
    .replace('.', '');
}

/**
 * How far a moment is from today, in calendar days: "vandaag", "morgen",
 * "over 3 dagen", "4 dagen geleden". Weeks from a week on, months from five.
 */
export function formatRelativeDay(isoTimestamp: string, now = new Date()): string {
  const days = daysFromToday(isoTimestamp, now);
  const format = new Intl.RelativeTimeFormat(getLocale(), { numeric: 'auto' });

  if (Math.abs(days) < NAMED_DAYS) return format.format(days, 'day');
  if (Math.abs(days) < 35) return format.format(Math.round(days / 7), 'week');
  return format.format(Math.round(days / 30), 'month');
}

/** "25 september", or "25 september 2025" once it is another year's. */
export function formatPastDay(isoTimestamp: string, now = new Date()): string {
  const date = new Date(isoTimestamp);
  if (date.getFullYear() === now.getFullYear()) return formatDayAndMonth(date);
  return new Intl.DateTimeFormat(getLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** "dinsdag 30 september" — the full day under a named one. */
export function formatFullDay(isoTimestamp: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(isoTimestamp));
}

function daysFromToday(isoTimestamp: string, now: Date): number {
  return Math.round((startOfDay(new Date(isoTimestamp)) - startOfDay(now)) / MS_PER_DAY);
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/* Monday: the work week a day belongs to. */
function startOfWeek(date: Date): number {
  const sinceMonday = (date.getDay() + 6) % 7;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - sinceMonday,
  ).getTime();
}
